import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { QrCode, Download, Copy, Check } from 'lucide-react';
import api from '@/lib/api';
import { useToast } from '@/hooks/useToast';

export function TagsUnboxing() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [desconto, setDesconto] = useState('10');
  const [limiteUsos, setLimiteUsos] = useState('2');
  const [codigo, setCodigo] = useState('');
  const [generatedQR, setGeneratedQR] = useState<{ url: string; base64: string; cupom: string; senha?: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setGeneratedQR(null);

    try {
      // Usa código limpo ou gera um automático
      const finalCode = codigo.trim() || `SALA${Math.floor(Math.random() * 10000)}B${Math.floor(Math.random() * 99)}`;

      const response = await api.post('/unboxing/gerar-avulso', { 
        codigo: finalCode, 
        desconto,
        limiteUsos
      });

      setGeneratedQR({
        url: response.data.unboxingUrl,
        base64: response.data.qrCodeBase64,
        cupom: response.data.cupom,
        senha: response.data.senha
      });
      toast({ title: 'Sucesso', description: 'QR Code gerado e linkado à Sala Secreta!' });
    } catch (error: any) {
      toast({ 
        title: 'Erro', 
        description: error.response?.data?.error || 'Erro ao gerar QR Code', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (generatedQR?.url) {
      navigator.clipboard.writeText(generatedQR.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Tags de Unboxing (Sala Secreta)</h1>
        <p className="text-gray-500 mt-2">
          Gere QR Codes personalizados vinculados a cupons de desconto. Os clientes escanearão a tag na caixa do pedido para acessar a Sala Secreta.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <Card className="shadow-lg border-gray-100">
          <CardHeader>
            <CardTitle>Configurar Nova Tag</CardTitle>
            <CardDescription>Defina o desconto e gela o código de acesso único.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleGenerate} className="space-y-5">
              <Input
                label="Valor do Desconto (%)"
                type="number"
                min="0"
                max="100"
                required
                value={desconto}
                onChange={(e) => setDesconto(e.target.value)}
              />
              <Input
                label="Limite de Vendas (0 = Ilimitado)"
                type="number"
                min="0"
                required
                value={limiteUsos}
                onChange={(e) => setLimiteUsos(e.target.value)}
              />
              <Input
                label="Código Opcional (Se vazio, será gerado automaticamente)"
                placeholder="Ex: TIKTOK15"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value.toUpperCase())}
              />
              <Button type="submit" variant="nuvem" className="w-full h-12 text-lg" isLoading={loading}>
                <QrCode className="w-5 h-5 mr-2" />
                Gerar QR Code de Acesso
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-gray-100 bg-gray-50/50 flex flex-col items-center justify-center p-8 text-center relative overflow-hidden">
          {generatedQR ? (
            <div className="space-y-6 flex flex-col items-center animate-in fade-in zoom-in duration-300">
              <div className="bg-white p-4 rounded-3xl shadow-xl shadow-primary/10 border border-gray-100">
                <img src={generatedQR.base64} alt="QR Code Unboxing" className="w-48 h-48" />
              </div>
              <div>
                <span className="inline-flex items-center gap-1.5 py-1 px-3 rounded-full bg-emerald-100 text-emerald-700 text-sm font-bold mb-2">
                  Cupom Ativo: {generatedQR.cupom}
                </span>
                {generatedQR.senha && (
                  <p className="text-gray-900 font-mono text-xs font-bold break-all bg-gray-100 p-2 rounded-lg mb-2">
                    Senha Unboxing: {generatedQR.senha}
                  </p>
                )}
                <p className="text-gray-500 text-sm break-all max-w-[280px]">
                  {generatedQR.url}
                </p>
              </div>
              <div className="flex gap-3 w-full">
                <Button variant="outline" className="flex-1" onClick={handleCopy}>
                  {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? 'Copiado!' : 'Copiar Link'}
                </Button>
                <Button 
                  className="flex-1" 
                  onClick={() => {
                    const a = document.createElement('a');
                    a.href = generatedQR.base64;
                    a.download = `qrcode_${generatedQR.cupom}.png`;
                    a.click();
                  }}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Baixar PNG
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-gray-400 flex flex-col items-center space-y-4">
              <div className="w-24 h-24 rounded-3xl bg-gray-100 flex items-center justify-center">
                <QrCode className="w-10 h-10 text-gray-300" />
              </div>
              <p>Preencha os dados e gere a tag para visualizá-la aqui.</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
